// frontend/src/pages/Proprietarios.js
import React, { useEffect, useState } from "react";
import "./Proprietarios.css";
import { jwtDecode } from "jwt-decode";
import { copiarTexto } from "../utils/clipboard";
import ThemeProvider from "../theme/TemaMuiProvider";
import { Box, Button, TextField, TablePagination, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import muiTheme from "../theme/muiTheme";
import { FaEdit } from "react-icons/fa";
import { limparCpfCnpj, mascararCnpj, mascararCpfCnpj, pareceCnpj, tipoDocumento } from "../utils/documento";
import DocumentoEstrangeiroCamposHtml, { FlagEstrangeiroHtml } from "../components/DocumentoEstrangeiroCamposHtml";
import {
  DOCUMENTO_ESTRANGEIRO_VAZIO,
  camposDocumentoEstrangeiro,
  descreverDocumentoEstrangeiro,
  validarDocumentoEstrangeiro,
} from "../utils/documentoEstrangeiro";

// =======================
// MÁSCARAS CPF/CNPJ
// =======================
function maskCPF(value) {
  return String(value ?? "")
    .replace(/\D/g, "")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2")
    .substring(0, 14);
}

// CNPJ numérico ou alfanumérico (letras A-Z nas 12 primeiras posições).
function maskCNPJ(value) {
  return mascararCnpj(value);
}

function identificarTipoDocumento(value) {
  const documento = limparCpfCnpj(value);

  if (documento.length === 0) {
    return "";
  }

  return pareceCnpj(documento) ? "Jurídica" : "Física";
}

function maskDocumento(value) {
  return mascararCpfCnpj(value);
}

// MÁSCARA CEP
function maskCEP(value) {
  return String(value ?? "")
    .replace(/\D/g, "")
    .replace(/(\d{5})(\d)/, "$1-$2")
    .substring(0, 9);
}

// CONSULTA VIA CEP
async function buscarCEP(cep) {
  try {
    const semMascara = String(cep ?? "").replace(/\D/g, "");
    if (semMascara.length !== 8) return;

    const res = await fetch(`https://viacep.com.br/ws/${semMascara}/json/`);
    const data = await res.json();
    if (data.erro) return;

    return {
      rua: data.logradouro,
      bairro: data.bairro,
      cidade: data.localidade,
      estado: data.uf,
    };
  } catch (e) {
    console.error("Erro ao consultar CEP:", e);
  }
}

function Proprietarios() {
  const [proprietarios, setProprietarios] = useState([]);
  const [gruposProprietario, setGruposProprietario] = useState([]);
  const [responsaveisLegais, setResponsaveisLegais] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [novoProprietario, setNovoProprietario] = useState({
    nome: "",
    grupo: "",
    representanteLegalId: "",
    tipoPessoa: "",
    documento: "",
    nacionalidade: "",
    estadoCivil: "",
    cep: "",
    rua: "",
    numero: "",
    bairro: "",
    cidade: "",
    estado: "",
    complemento: "",
    ...DOCUMENTO_ESTRANGEIRO_VAZIO,
  });

  const [editandoId, setEditandoId] = useState(null);
  const [loading, setLoading] = useState(true);

  const [paginaAtual, setPaginaAtual] = useState(1);
  const [proprietariosPorPagina, setProprietariosPorPagina] = useState(5);

  // NOVOS ESTADOS: VISUALIZAÇÃO
  const [modoVisualizacao, setModoVisualizacao] = useState(false);
  const [idVisualizando, setIdVisualizando] = useState(null);

  // pendências
  const [mostrarPendencias, setMostrarPendencias] = useState(false);
  const [nomeBloqueadoReceita, setNomeBloqueadoReceita] = useState(false);
  const [camposReceitaEditaveis, setCamposReceitaEditaveis] = useState([]);

  // ✅ minimizar/expandir grid (persistido) — IGUAL Usuários
  const [gridMinimizado, setGridMinimizado] = useState(() => {
    const saved = localStorage.getItem("proprietariosGridMinimizado");
    return saved ? saved === "1" : false;
  });

  useEffect(() => {
    localStorage.setItem("proprietariosGridMinimizado", gridMinimizado ? "1" : "0");
  }, [gridMinimizado]);

  // SISTEMA DE PERMISSÕES
  const token = localStorage.getItem("token");
  let tipoUsuario = "";

  if (token) {
    const decoded = jwtDecode(token);
    tipoUsuario = decoded.tipo
      ?.toString()
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }

  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeCadastrar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeEditar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeExcluir = ["gestor", "administrador"].includes(tipoUsuario);

  // =======================
  // CARREGAR LISTA
  // =======================
  useEffect(() => {
    carregarProprietarios();
    carregarGruposProprietario();
    carregarResponsaveisLegais();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const carregarProprietarios = () => {
    const token = localStorage.getItem("token");

    fetch("/api/proprietarios", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        const lista = Array.isArray(data) ? data : data.proprietarios || [];
        setProprietarios(lista);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Erro ao buscar proprietários:", err);
        setLoading(false);
      });
  };

  const carregarGruposProprietario = () => {
    const token = localStorage.getItem("token");

    fetch("/api/grupo-proprietarios", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        const lista = Array.isArray(data) ? data : Array.isArray(data.grupos) ? data.grupos : [];

        lista.sort((a, b) => (a.nome || "").localeCompare(b.nome || "", "pt-BR"));

        setGruposProprietario(lista);
      })
      .catch((err) => {
        console.error("Erro ao buscar grupos de proprietários:", err);
        setGruposProprietario([]);
      });
  };

  const carregarResponsaveisLegais = () => {
    const token = localStorage.getItem("token");

    fetch("/api/responsaveis-legais", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Erro HTTP ${res.status}`);
        }

        return res.json();
      })
      .then((data) => {
        const lista = Array.isArray(data) ? data : Array.isArray(data?.responsaveisLegais) ? data.responsaveisLegais : [];

        lista.sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"));

        setResponsaveisLegais(lista);
      })
      .catch((err) => {
        console.error("Erro ao buscar responsáveis legais:", err);

        setResponsaveisLegais([]);
      });
  };

  const resetForm = () => {
    setNovoProprietario({
      nome: "",
      grupo: "",
      representanteLegalId: "",
      tipoPessoa: "",
      documento: "",
      nacionalidade: "",
      estadoCivil: "",
      cep: "",
      rua: "",
      numero: "",
      bairro: "",
      cidade: "",
      estado: "",
      complemento: "",
      ...DOCUMENTO_ESTRANGEIRO_VAZIO,
    });

    setEditandoId(null);
    setIdVisualizando(null);
    setModoVisualizacao(false);
    setMostrarPendencias(false);
    setNomeBloqueadoReceita(false);
    setCamposReceitaEditaveis([]);
  };

  // =======================
  // FILTRO AVANÇADO
  // =======================
  const termos = filtro
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);

  const filtrados = proprietarios.filter((p) =>
    termos.every((termo) =>
      [
        p.nome,
        p.grupo,
        p.nacionalidade,
        p.estadoCivil,
        p.tipoPessoa,
        p.cpf,
        p.cnpj,
        descreverDocumentoEstrangeiro(p),
        p.cep,
        p.cidade,
        p.estado,
        p.bairro,
        p.rua,
        p.numero,
        p.complemento,
      ]
        .join(" ")
        .toLowerCase()
        .includes(termo),
    ),
  );

  const totalRegistros = filtrados.length;
  const totalPaginas = Math.max(1, Math.ceil(totalRegistros / proprietariosPorPagina));
  const indexInicial = (paginaAtual - 1) * proprietariosPorPagina;
  const indexFinal = Math.min(indexInicial + proprietariosPorPagina, totalRegistros);
  const registrosPaginados = filtrados.slice(indexInicial, indexFinal);

  // Form travado quando está só visualizando
  const formTravado = modoVisualizacao && !editandoId;

  // Mostra excluir no form quando registro aberto
  const temRegistroAberto = Boolean(idVisualizando || editandoId);
  const idParaExcluirNoForm = editandoId || idVisualizando;

  // ==============================
  // CAMPOS OPCIONAIS VAZIOS EM VERMELHO
  // ==============================
  const camposOpcionais = [
    "grupo",
    "representanteLegalId",
    "nacionalidade",
    "estadoCivil",
    "cep",
    "rua",
    "numero",
    "bairro",
    "cidade",
    "estado",
    "complemento",
  ];

  const estaVazio = (v) => !String(v ?? "").trim();

  const classeCampoOpcional = (campo) => {
    if (!mostrarPendencias) return "";
    return camposOpcionais.includes(campo) && estaVazio(novoProprietario[campo]) ? "campo-pendente" : "";
  };
  const classeCampoReceitaEditavel = (campo) => {
    return camposReceitaEditaveis.includes(campo) ? "campo-receita-editavel" : "";
  };

  const consultarDocumento = async (tipoPessoa, documento) => {
    const docLimpo = limparCpfCnpj(documento);

    if (tipoPessoa === "Física" && docLimpo.length !== 11) return;
    if (tipoPessoa === "Jurídica" && docLimpo.length !== 14) return;

    try {
      const token = localStorage.getItem("token");
      const tipo = tipoPessoa === "Física" ? "PF" : "PJ";

      const res = await fetch(`/api/proprietarios/consulta-documento?tipo=${tipo}&documento=${docLimpo}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        setNomeBloqueadoReceita(false);
        setCamposReceitaEditaveis([]);
        return;
      }

      const data = await res.json();
      const nomeVeioDaReceita = Boolean(data.nome);

      setNomeBloqueadoReceita(nomeVeioDaReceita);

      setCamposReceitaEditaveis(["nacionalidade", "cep", "rua", "numero", "bairro", "cidade", "estado", "complemento"]);

      setNovoProprietario((prev) => ({
        ...prev,
        nome: data.nome || prev.nome,
        nacionalidade:
          tipoPessoa === "Jurídica" ? data.nacionalidade || prev.nacionalidade || "BRASIL" : data.nacionalidade || prev.nacionalidade || "BRASILEIRO",
        grupo: data.grupo || prev.grupo,
        cep: data.cep ? maskCEP(data.cep) : prev.cep,
        rua: data.rua || prev.rua,
        numero: data.numero || prev.numero,
        bairro: data.bairro || prev.bairro,
        cidade: data.cidade || prev.cidade,
        estado: data.estado || prev.estado,
        complemento: data.complemento || prev.complemento,
      }));
    } catch (err) {
      console.error("Erro ao consultar CPF/CNPJ:", err);
      setNomeBloqueadoReceita(false);
      setCamposReceitaEditaveis([]);
    }
  };

  // =======================
  // CADASTRAR / ATUALIZAR
  // =======================
  const handleCadastrar = (e) => {
    e.preventDefault();

    setMostrarPendencias(true);

    if (!editandoId && !podeCadastrar) return alert("Você não tem permissão para cadastrar.");
    if (editandoId && !podeEditar) return alert("Você não tem permissão para editar.");

    const token = localStorage.getItem("token");

    if (!novoProprietario.nome.trim()) {
      alert("Preencha o nome do proprietário.");
      return;
    }

    const docLimpo = limparCpfCnpj(novoProprietario.documento);

    // Cliente estrangeiro: documento estrangeiro obrigatório e CPF/CNPJ opcional.
    if (novoProprietario.estrangeiro) {
      const errosEstrangeiro = Object.values(validarDocumentoEstrangeiro(novoProprietario, { temDocumentoNacional: Boolean(docLimpo) }));
      if (errosEstrangeiro.length) {
        alert(errosEstrangeiro[0]);
        return;
      }
      if (docLimpo && !tipoDocumento(docLimpo)) {
        alert("Documento inválido. Informe um CPF com 11 números ou um CNPJ com 14 caracteres, ou deixe em branco.");
        return;
      }
      if (!docLimpo && !["Física", "Jurídica"].includes(novoProprietario.tipoPessoa)) {
        alert("Informe o tipo de pessoa do proprietário estrangeiro.");
        return;
      }
    } else if (!tipoDocumento(docLimpo)) {
      alert("Documento inválido. Informe um CPF com 11 números ou um CNPJ com 14 caracteres.");
      return;
    }

    const tipoIdentificado = docLimpo ? (tipoDocumento(docLimpo) === "CPF" ? "Física" : "Jurídica") : novoProprietario.tipoPessoa;

    const tipoBackend = tipoIdentificado === "Física" ? "fisica" : "juridica";

    const payload = {
      nome: novoProprietario.nome,
      grupo: novoProprietario.grupo,
      representanteLegalId: novoProprietario.representanteLegalId ? Number(novoProprietario.representanteLegalId) : null,
      tipoPessoa: tipoBackend,

      cpf: tipoBackend === "fisica" ? docLimpo : "",
      cnpj: tipoBackend === "juridica" ? docLimpo : "",

      nacionalidade: novoProprietario.nacionalidade,
      estadoCivil: tipoBackend === "fisica" ? novoProprietario.estadoCivil : "",
      cep: novoProprietario.cep,
      rua: novoProprietario.rua,
      numero: novoProprietario.numero,
      bairro: novoProprietario.bairro,
      cidade: novoProprietario.cidade,
      estado: novoProprietario.estado,
      complemento: novoProprietario.complemento,
      ...camposDocumentoEstrangeiro(novoProprietario),
    };

    const id = editandoId;
    const url = id ? `/api/proprietarios/${id}` : "/api/proprietarios";
    const method = id ? "PUT" : "POST";

    fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((data) => {
        const idAtual = editandoId;

        if (idAtual) {
          setProprietarios((prev) =>
            prev.map((p) =>
              String(p._id || p.id) === String(idAtual)
                ? {
                    ...p,
                    ...payload,
                    id: p.id || idAtual,
                    _id: p._id,
                  }
                : p,
            ),
          );

          resetForm();
          return;
        }

        const idNovo = data?.id || data?._id || data?.proprietario?.id || data?.proprietario?._id || Date.now();

        const novoRegistro = {
          id: idNovo,
          ...payload,
        };

        setProprietarios((prev) => [novoRegistro, ...prev]);
        setPaginaAtual(1);
        resetForm();
      })
      .catch((err) => console.error("Erro ao salvar proprietário:", err));
  };

  // =======================
  // VISUALIZAR
  // =======================
  const handleVisualizar = (p) => {
    if (!podeVisualizar) return alert("Você não tem permissão para visualizar.");

    const id = p._id || p.id;

    const tipoTela = p.tipoPessoa === "fisica" ? "Física" : p.tipoPessoa === "juridica" ? "Jurídica" : "Nome Fantasia";

    const doc = p.tipoPessoa === "fisica" ? maskCPF(p.cpf || "") : p.tipoPessoa === "juridica" ? maskCNPJ(p.cnpj || "") : "";

    setNovoProprietario({
      nome: p.nome,
      grupo: p.grupo,
      representanteLegalId: p.representanteLegalId || "",
      tipoPessoa: tipoTela,
      documento: doc,
      estrangeiro: Boolean(p.estrangeiro),
      documentoEstrangeiroTipo: p.documentoEstrangeiroTipo || "",
      documentoEstrangeiroNumero: p.documentoEstrangeiroNumero || "",
      documentoEstrangeiroPais: p.documentoEstrangeiroPais || "",
      nacionalidade: p.nacionalidade || "",
      estadoCivil: p.estadoCivil || "",
      cep: maskCEP(p.cep || ""),
      rua: p.rua || "",
      numero: p.numero || "",
      bairro: p.bairro || "",
      cidade: p.cidade || "",
      estado: p.estado || "",
      complemento: p.complemento || "",
    });

    setIdVisualizando(id);
    setEditandoId(null);
    setModoVisualizacao(true);
    setMostrarPendencias(true);

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // =======================
  // EXCLUIR
  // =======================
  const handleExcluir = (pOuId) => {
    if (!podeExcluir) return alert("Você não tem permissão para excluir.");

    const token = localStorage.getItem("token");

    const isObj = typeof pOuId === "object" && pOuId !== null;
    const id = isObj ? pOuId._id || pOuId.id : pOuId;
    const nome = isObj ? pOuId.nome : novoProprietario.nome;

    if (!id) {
      alert("Selecione um proprietário para excluir.");
      return;
    }

    if (window.confirm(`Excluir proprietário "${nome}"?`)) {
      fetch(`/api/proprietarios/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => {
          if (!res.ok) throw new Error("Erro ao excluir");
          carregarProprietarios();
          if (idVisualizando === id || editandoId === id) resetForm();
        })
        .catch((err) => console.error("Erro ao excluir proprietário:", err));
    }
  };

  // =======================
  // EXPORTAR CSV
  // =======================
  const handleCSV = () => {
    const BOM = "\uFEFF";

    const header = ["Nome", "Grupo", "Tipo", "Documento", "Nacionalidade", "Estado Civil", "CEP", "Cidade", "Estado", "Bairro", "Rua", "Número", "Complemento"];

    const csvEscape = (value) => {
      const s = String(value ?? "");
      if (/[;"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };

    const excelText = (value) => `="${String(value ?? "")}"`;

    const linhas = (filtrados || []).map((p) => {
      const tipo = p.tipoPessoa ?? "";
      const doc = (tipo === "fisica" ? maskCPF(p.cpf || "") : tipo === "juridica" ? maskCNPJ(p.cnpj || "") : "") || descreverDocumentoEstrangeiro(p);

      return [
        p.nome ?? "",
        p.grupo ?? "",
        tipo ?? "",
        excelText(doc),
        p.nacionalidade ?? "",
        p.estadoCivil ?? "",
        excelText(maskCEP(p.cep || "")),
        p.cidade ?? "",
        p.estado ?? "",
        p.bairro ?? "",
        p.rua ?? "",
        excelText(p.numero ?? ""),
        p.complemento ?? "",
      ].map(csvEscape);
    });

    const csv = [header.map(csvEscape), ...linhas].map((l) => l.join(";")).join("\n");

    const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "proprietarios.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <div className="local-container">
        <style>{`
        .campo-formulario-padrao,
        .campo-formulario-padrao:focus,
        .campo-formulario-padrao:active,
        .campo-formulario-padrao:read-only,
        .campo-formulario-padrao:disabled {
          background: transparent !important;
          background-color: transparent !important;
          color: var(--ig-t-ffffff) !important;
          box-shadow: none !important;
          -webkit-text-fill-color: var(--ig-t-ffffff) !important;
          opacity: 1 !important;
        }

        .campo-formulario-padrao::placeholder {
          color: var(--ig-t-255-255-255-a072) !important;
          -webkit-text-fill-color: var(--ig-t-255-255-255-a072) !important;
        }

        .campo-formulario-padrao::-webkit-calendar-picker-indicator {
          filter: var(--ig-x-11pexw5);
          opacity: 0.9;
          cursor: pointer;
        }
      `}</style>

        <h2>Proprietários</h2>

        {/* FILTRO (sempre visível) */}
        <Box
          className="bloco-linha"
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) auto",
            alignItems: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              height: 52,
              width: "100%",
              border: "1px solid var(--ig-b-555555)",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              backgroundColor: "transparent",
              boxSizing: "border-box",
              px: 2,
            }}
          >
            <input
              type="text"
              placeholder="Buscar Proprietário"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              style={{
                flex: 1,
                height: "100%",
                border: "none",
                outline: "none",
                background: "transparent",
                color: "var(--ig-t-ffffff)",
                fontSize: "1rem",
              }}
            />

            <SearchIcon
              sx={{
                color: "var(--ig-t-9ca3af)",
                fontSize: 20,
                cursor: "pointer",
                flexShrink: 0,
              }}
            />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              type="button"
              onClick={() => setFiltro("")}
              sx={{
                height: 52,
                color: "#2196f3",
                borderColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  borderColor: "#42a5f5",
                  backgroundColor: "rgba(33, 150, 243, 0.08)",
                },
              }}
            >
              Limpar Filtro
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={async () => {
                const texto = filtrados.map((p) => p.nome).join(", ");
                if (!texto.trim()) {
                  alert("Nada para copiar (lista vazia).");
                  return;
                }
                const ok = await copiarTexto(texto);
                alert(ok ? "Copiado!" : "Não foi possível copiar.");
              }}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              <i className="fas fa-copy" style={{ marginRight: 6 }}></i>
              Copiar
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={handleCSV}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              CSV
            </Button>
          </Box>
        </Box>

        {/* ✅ BOTÃO MINIMIZAR/EXPANDIR */}
        <div className="grid-toggle-area right">
          {!gridMinimizado ? (
            <button
              type="button"
              className="btn-grid-toggle small danger"
              onClick={() => setGridMinimizado(true)}
              title="Minimizar o Grid"
              aria-label="Minimizar o Grid"
            >
              ↘ Minimizar
            </button>
          ) : (
            <button
              type="button"
              className="btn-grid-toggle small outline"
              onClick={() => setGridMinimizado(false)}
              title="Expandir o Grid"
              aria-label="Expandir o Grid"
            >
              ↗ Expandir
            </button>
          )}
        </div>

        {/* LISTA */}
        {!gridMinimizado && (
          <div className="bloco-lista">
            {loading ? (
              <p>Carregando Proprietários...</p>
            ) : filtrados.length === 0 ? (
              <p className="erro">Nenhum proprietário encontrado.</p>
            ) : (
              <>
                <table>
                  <thead>
                    <tr>
                      <th>Nome do Proprietário</th>
                      <th>Grupo</th>
                      <th>Pessoa</th>
                      <th>CPF | CNPJ</th>
                      <th>Ações</th>
                    </tr>
                  </thead>

                  <tbody>
                    {registrosPaginados.map((p) => (
                      <tr key={p._id || p.id}>
                        <td>{p.nome}</td>
                        <td>{p.grupo}</td>
                        <td>{p.tipoPessoa === "fisica" ? "FÍSICA" : p.tipoPessoa === "juridica" ? "JURÍDICA" : "NOME FANTASIA"}</td>
                        <td>
                          {(p.tipoPessoa === "fisica" ? maskCPF(p.cpf || "") : p.tipoPessoa === "juridica" ? maskCNPJ(p.cnpj || "") : "") ||
                            descreverDocumentoEstrangeiro(p)}
                        </td>

                        <td className="col-acoes">
                          {podeVisualizar && (
                            <button className="btn-editar" type="button" onClick={() => handleVisualizar(p)}>
                              <i className="fas fa-eye"></i> Visualizar
                            </button>
                          )}

                          {podeExcluir && (
                            <button className="btn-excluir" type="button" onClick={() => handleExcluir(p)}>
                              <i className="fas fa-trash"></i> Excluir
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <TablePagination
                  component="div"
                  count={filtrados.length}
                  page={paginaAtual - 1}
                  onPageChange={(_, novaPagina) => setPaginaAtual(novaPagina + 1)}
                  rowsPerPage={proprietariosPorPagina}
                  onRowsPerPageChange={(e) => {
                    setProprietariosPorPagina(parseInt(e.target.value, 10));
                    setPaginaAtual(1);
                  }}
                  rowsPerPageOptions={[5, 10, 25]}
                  labelRowsPerPage="Proprietários por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                  sx={{
                    fontSize: "0.8rem",
                    minHeight: 42,
                    color: "var(--ig-t-ffffff)",
                    borderTop: "1px solid var(--ig-b-444444)",
                    "& .MuiTablePagination-toolbar": {
                      minHeight: 42,
                    },
                    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                      fontSize: "0.8rem",
                    },
                    "& .MuiTablePagination-selectIcon": {
                      color: "var(--ig-t-ffffff)",
                    },
                    "& .MuiTablePagination-actions button": {
                      color: "var(--ig-t-ffffff)",
                      width: "auto",
                    },
                    "& .MuiTablePagination-actions": {
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                />
              </>
            )}
          </div>
        )}

        {/* FORMULÁRIO */}
        {(podeCadastrar || podeVisualizar) && (
          <div className="bloco-formulario">
            <form onSubmit={handleCadastrar} className="local-form">
              {/* Linha 1: Documento com identificação automática CPF/CNPJ */}
              <div className="linha-dupla" style={{ gap: 12, alignItems: "flex-end" }}>
                <div style={{ flex: "1 1 0", minWidth: 280 }}>
                  <label>{novoProprietario.estrangeiro ? "Documento (opcional)" : "Documento*"}</label>

                  <div style={{ position: "relative" }}>
                    <input
                      value={novoProprietario.documento}
                      onChange={async (e) => {
                        if (formTravado) return;

                        const numeros = limparCpfCnpj(e.target.value);

                        const tipoIdentificado = identificarTipoDocumento(numeros);

                        const documentoFormatado = maskDocumento(numeros);

                        setNovoProprietario((prev) => ({
                          ...prev,
                          // Estrangeiro sem CPF/CNPJ mantém o tipo escolhido manualmente.
                          tipoPessoa: prev.estrangeiro && !numeros ? prev.tipoPessoa : tipoIdentificado,
                          documento: documentoFormatado,
                        }));

                        if (tipoDocumento(numeros) === "CPF") {
                          await consultarDocumento("Física", numeros);
                        }

                        if (tipoDocumento(numeros) === "CNPJ") {
                          await consultarDocumento("Jurídica", numeros);
                        }
                      }}
                      placeholder="Digite o CPF ou CNPJ"
                      required={!formTravado && !novoProprietario.estrangeiro}
                      disabled={formTravado}
                      inputMode="text"
                      className="campo-formulario-padrao"
                      style={{
                        paddingRight: "110px",
                      }}
                    />

                    {novoProprietario.tipoPessoa && novoProprietario.documento && (
                      <span
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: novoProprietario.tipoPessoa === "Jurídica" ? "#42a5f5" : "var(--ig-t-26c6da)",
                          fontSize: "0.78rem",
                          fontWeight: 700,
                          pointerEvents: "none",
                        }}
                      >
                        {novoProprietario.tipoPessoa === "Física" ? "CPF" : "CNPJ"}
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ flex: "0 0 auto", paddingBottom: 10 }}>
                  <FlagEstrangeiroHtml
                    valor={novoProprietario}
                    onChange={(campo, valor) => setNovoProprietario((prev) => ({ ...prev, [campo]: valor }))}
                    somenteLeitura={formTravado}
                    rotulo="Proprietário estrangeiro"
                  />
                </div>

                <div style={{ flex: "2 1 0", minWidth: 220 }}>
                  <label>Nome do Proprietário*</label>

                  <input
                    value={novoProprietario.nome}
                    onChange={(e) =>
                      setNovoProprietario({
                        ...novoProprietario,
                        nome: e.target.value.toUpperCase(),
                      })
                    }
                    placeholder="Digite o nome"
                    required
                    disabled={formTravado || nomeBloqueadoReceita}
                    className="campo-formulario-padrao"
                  />
                </div>
              </div>

              {/* Proprietário estrangeiro: documento estrangeiro no lugar do CPF/CNPJ */}
              <DocumentoEstrangeiroCamposHtml
                obrigatorio={!limparCpfCnpj(novoProprietario.documento)}
                mostrarFlag={false}
                valor={novoProprietario}
                onChange={(campo, valor) => setNovoProprietario((prev) => ({ ...prev, [campo]: valor }))}
                somenteLeitura={formTravado}
                estiloSelect={{ backgroundColor: "var(--ig-f-1e1e1e)", color: "var(--ig-t-ffffff)" }}
                mostrarTipoPessoa={!tipoDocumento(novoProprietario.documento)}
                tipoPessoa={novoProprietario.tipoPessoa}
                onTipoPessoa={(valor) => setNovoProprietario((prev) => ({ ...prev, tipoPessoa: valor }))}
                opcoesTipoPessoa={[
                  { valor: "Física", label: "Pessoa Física" },
                  { valor: "Jurídica", label: "Pessoa Jurídica" },
                ]}
              />

              {/* Linha 2: Grupo + Representante Legal + País de Origem/Nacionalidade */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.35fr 1.35fr 0.8fr",
                  gap: 12,
                }}
              >
                <div>
                  <label>Grupo do Proprietário</label>

                  <select
                    value={novoProprietario.grupo}
                    onChange={(e) =>
                      setNovoProprietario({
                        ...novoProprietario,
                        grupo: e.target.value,
                      })
                    }
                    disabled={formTravado}
                    className={`campo-formulario-padrao ${classeCampoOpcional("grupo")}`.trim()}
                    style={{
                      backgroundColor: "var(--ig-f-1e1e1e)",
                      color: "var(--ig-t-ffffff)",
                    }}
                  >
                    <option
                      value=""
                      style={{
                        backgroundColor: "var(--ig-f-1e1e1e)",
                        color: "var(--ig-t-ffffff)",
                      }}
                    >
                      Selecione um Grupo
                    </option>

                    {gruposProprietario.length === 0 ? (
                      <option
                        value=""
                        disabled
                        style={{
                          backgroundColor: "var(--ig-f-1e1e1e)",
                          color: "var(--ig-t-ffffff)",
                        }}
                      >
                        Nenhum grupo cadastrado
                      </option>
                    ) : (
                      gruposProprietario.map((grupo) => (
                        <option
                          key={grupo.id}
                          value={grupo.nome}
                          style={{
                            backgroundColor: "var(--ig-f-1e1e1e)",
                            color: "var(--ig-t-ffffff)",
                          }}
                        >
                          {grupo.nome}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div>
                  <label>Representante Legal</label>

                  <select
                    value={novoProprietario.representanteLegalId}
                    onChange={(e) =>
                      setNovoProprietario({
                        ...novoProprietario,
                        representanteLegalId: e.target.value,
                      })
                    }
                    disabled={formTravado}
                    className={`campo-formulario-padrao ${classeCampoOpcional("representanteLegalId")}`.trim()}
                    style={{
                      backgroundColor: "var(--ig-f-1e1e1e)",
                      color: "var(--ig-t-ffffff)",
                    }}
                  >
                    <option
                      value=""
                      style={{
                        backgroundColor: "var(--ig-f-1e1e1e)",
                        color: "var(--ig-t-ffffff)",
                      }}
                    >
                      Selecione um Representante Legal
                    </option>

                    {responsaveisLegais.length === 0 ? (
                      <option
                        value=""
                        disabled
                        style={{
                          backgroundColor: "var(--ig-f-1e1e1e)",
                          color: "var(--ig-t-ffffff)",
                        }}
                      >
                        Nenhum representante legal cadastrado
                      </option>
                    ) : (
                      responsaveisLegais.map((responsavel) => (
                        <option
                          key={responsavel.id}
                          value={responsavel.id}
                          style={{
                            backgroundColor: "var(--ig-f-1e1e1e)",
                            color: "var(--ig-t-ffffff)",
                          }}
                        >
                          {responsavel.nome}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div>
                  <label>{novoProprietario.tipoPessoa === "Jurídica" ? "País de Origem" : "Nacionalidade"}</label>

                  <input
                    value={novoProprietario.nacionalidade}
                    onChange={(e) =>
                      setNovoProprietario({
                        ...novoProprietario,
                        nacionalidade: e.target.value.toUpperCase(),
                      })
                    }
                    disabled={formTravado}
                    className={`campo-formulario-padrao ${classeCampoOpcional("nacionalidade")} ${classeCampoReceitaEditavel("nacionalidade")}`.trim()}
                  />
                </div>
              </div>

              {/* Estado Civil - somente Pessoa Física */}
              {novoProprietario.tipoPessoa === "Física" && (
                <div>
                  <label>Estado Civil</label>

                  <select
                    value={novoProprietario.estadoCivil}
                    onChange={(e) =>
                      setNovoProprietario({
                        ...novoProprietario,
                        estadoCivil: e.target.value,
                      })
                    }
                    disabled={formTravado}
                    className={`campo-formulario-padrao ${classeCampoOpcional("estadoCivil")}`.trim()}
                    style={{
                      backgroundColor: "var(--ig-f-1e1e1e)",
                      color: "var(--ig-t-ffffff)",
                    }}
                  >
                    <option value="">Selecione o Estado Civil</option>
                    <option>Solteiro(a)</option>
                    <option>Casado(a)</option>
                    <option>Divorciado(a)</option>
                    <option>Viúvo(a)</option>
                    <option>União Estável</option>
                  </select>
                </div>
              )}

              {/* Linha 3: CEP + Rua + Número */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "0.75fr 1.7fr 0.55fr",
                  gap: 12,
                }}
              >
                <div>
                  <label>CEP</label>

                  <input
                    value={novoProprietario.cep}
                    onChange={async (e) => {
                      if (formTravado) return;

                      const cepFormatado = maskCEP(e.target.value);

                      setNovoProprietario((prev) => ({
                        ...prev,
                        cep: cepFormatado,
                      }));

                      if (cepFormatado.replace(/\D/g, "").length === 8) {
                        const endereco = await buscarCEP(cepFormatado);

                        if (endereco) {
                          setNovoProprietario((prev) => ({
                            ...prev,
                            rua: endereco.rua || "",
                            bairro: endereco.bairro || "",
                            cidade: endereco.cidade || "",
                            estado: endereco.estado || "",
                          }));
                        }
                      }
                    }}
                    placeholder="Digite o CEP"
                    disabled={formTravado}
                    className={`campo-formulario-padrao ${classeCampoOpcional("cep")} ${classeCampoReceitaEditavel("cep")}`.trim()}
                  />
                </div>

                <div>
                  <label>Rua</label>

                  <input
                    value={novoProprietario.rua}
                    onChange={(e) =>
                      setNovoProprietario({
                        ...novoProprietario,
                        rua: e.target.value.toUpperCase(),
                      })
                    }
                    disabled={formTravado}
                    className={`campo-formulario-padrao ${classeCampoOpcional("rua")} ${classeCampoReceitaEditavel("rua")}`.trim()}
                  />
                </div>

                <div>
                  <label>Número</label>

                  <input
                    value={novoProprietario.numero}
                    onChange={(e) =>
                      setNovoProprietario({
                        ...novoProprietario,
                        numero: e.target.value,
                      })
                    }
                    disabled={formTravado}
                    className={`campo-formulario-padrao ${classeCampoOpcional("numero")} ${classeCampoReceitaEditavel("numero")}`.trim()}
                  />
                </div>
              </div>

              {/* Linha 4: Complemento */}
              <div>
                <label>Complemento</label>

                <input
                  value={novoProprietario.complemento}
                  onChange={(e) =>
                    setNovoProprietario({
                      ...novoProprietario,
                      complemento: e.target.value.toUpperCase(),
                    })
                  }
                  disabled={formTravado}
                  className={`campo-formulario-padrao ${classeCampoOpcional("complemento")} ${classeCampoReceitaEditavel("complemento")}`.trim()}
                />
              </div>

              {/* Linha 5: Bairro + Cidade + Estado */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.45fr 1.15fr 0.4fr",
                  gap: 12,
                }}
              >
                <div>
                  <label>Bairro</label>

                  <input
                    value={novoProprietario.bairro}
                    onChange={(e) =>
                      setNovoProprietario({
                        ...novoProprietario,
                        bairro: e.target.value.toUpperCase(),
                      })
                    }
                    disabled={formTravado}
                    className={`campo-formulario-padrao ${classeCampoOpcional("bairro")} ${classeCampoReceitaEditavel("bairro")}`.trim()}
                  />
                </div>

                <div>
                  <label>Cidade</label>

                  <input
                    value={novoProprietario.cidade}
                    onChange={(e) =>
                      setNovoProprietario({
                        ...novoProprietario,
                        cidade: e.target.value.toUpperCase(),
                      })
                    }
                    disabled={formTravado}
                    className={`campo-formulario-padrao ${classeCampoOpcional("cidade")} ${classeCampoReceitaEditavel("cidade")}`.trim()}
                  />
                </div>

                <div>
                  <label>Estado</label>

                  <input
                    value={novoProprietario.estado}
                    onChange={(e) =>
                      setNovoProprietario({
                        ...novoProprietario,
                        estado: e.target.value.toUpperCase(),
                      })
                    }
                    disabled={formTravado}
                    className={`campo-formulario-padrao ${classeCampoOpcional("estado")} ${classeCampoReceitaEditavel("estado")}`.trim()}
                  />
                </div>
              </div>

              {/* Ações do formulário */}
              <div className="acoes-form-proprietarios">
                {modoVisualizacao && !editandoId && podeEditar && (
                  <button
                    type="button"
                    className="btn-cadastrar"
                    onClick={() => {
                      setEditandoId(idVisualizando);
                      setModoVisualizacao(false);
                    }}
                  >
                    <FaEdit /> Editar
                  </button>
                )}

                {(!modoVisualizacao || editandoId) && (
                  <button type="submit" className="btn-cadastrar">
                    <i className="fas fa-save"></i>

                    <span>{editandoId ? "Atualizar" : "Cadastrar"}</span>
                  </button>
                )}

                {(modoVisualizacao || editandoId) && (
                  <button type="button" className="btn-cancelar" onClick={resetForm}>
                    <i className="fas fa-times"></i> Cancelar
                  </button>
                )}

                {temRegistroAberto && podeExcluir && (
                  <button type="button" className="btn-excluir" onClick={() => handleExcluir(idParaExcluirNoForm)}>
                    <i className="fas fa-trash"></i> Excluir
                  </button>
                )}
              </div>
            </form>
          </div>
        )}
      </div>
    </ThemeProvider>
  );
}

export default Proprietarios;
